import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { CompanyLogo } from "../../components/CompanyLogo";
import { ErrorBox, FreshnessStamp, Skeleton } from "../../components/ui";
import { useOrgChart, type OrgChart as OrgChartData } from "../../lib/api";
import { SectionCard } from "./shared";

type Node = OrgChartData["tree"][number];

/** Reporting lines (WP-23.1). Its own screen because it is the heaviest thing on the lifecycle to render — one
 * node per employee — and almost nobody opening the approval queue wants it. */
export default function OrgChart({ company }: { company: string }) {
  const { t } = useTranslation();
  const q = useOrgChart(company || undefined);

  if (q.isError) return <div className="mt-6"><ErrorBox error={q.error} onRetry={() => void q.refetch()} /></div>;
  if (!q.data) return <div className="mt-6"><Skeleton className="h-96" /></div>;

  return (
    <div className="mt-4 space-y-4" data-hr-org>
      <div className="flex justify-end"><FreshnessStamp lastUpdated={q.data.as_of} /></div>
      <SectionCard title={t("hr.orgChart")} aside={t("hr.orgHint")}>
        <div className="p-4 text-sm"><OrgTree nodes={q.data.tree} /></div>
      </SectionCard>
    </div>
  );
}

function OrgTree({ nodes }: { nodes: Node[] }) {
  const byManager = useMemo(() => {
    // the schema turns null into "" — an empty or unknown manager makes the node a root
    const ids = new Set(nodes.map((n) => n.id));
    const m = new Map<string | null, Node[]>();
    for (const n of nodes) {
      const key = n.reports_to && ids.has(n.reports_to) ? n.reports_to : null;
      m.set(key, [...(m.get(key) ?? []), n]);
    }
    return m;
  }, [nodes]);
  const roots = byManager.get(null) ?? [];
  return <ul className="space-y-1">{roots.map((n) => <OrgNode key={n.id} node={n} byManager={byManager} depth={0} />)}</ul>;
}

function OrgNode({ node, byManager, depth }: { node: Node; byManager: Map<string | null, Node[]>; depth: number }) {
  const children = byManager.get(node.id) ?? [];
  const [open, setOpen] = useState(depth < 2);
  return (
    <li>
      <div className={`flex items-center gap-2 rounded px-2 py-1 ${node.is_me ? "bg-violet-50" : ""}`} style={{ marginLeft: depth * 16 }}>
        {children.length > 0 ? <button onClick={() => setOpen((o) => !o)} className="w-4 text-xs text-brand-grey">{open ? "▾" : "▸"}</button> : <span className="w-4" />}
        <CompanyLogo company={node.company} size={12} />
        <span className={`font-semibold ${node.status === "Left" ? "line-through text-brand-grey" : ""}`}>{node.name}</span>
        <span className="text-xs text-brand-grey">{node.designation}{node.department ? ` · ${node.department}` : ""}</span>
        {children.length > 0 && <span className="rounded bg-brand-grey-light px-1.5 text-[10px]">{children.length}</span>}
      </div>
      {open && children.length > 0 && <ul>{children.map((c) => <OrgNode key={c.id} node={c} byManager={byManager} depth={depth + 1} />)}</ul>}
    </li>
  );
}
